import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';

import copy from 'copy-to-clipboard';
import globalMessages from 'translations/messages';

import { Button, Form, Input, Space, Tag, Typography } from 'antd';
import { CheckOutlined, DeleteFilled, EditFilled, LinkOutlined, StopOutlined, UnlockOutlined, UserAddOutlined, UserSwitchOutlined } from '@ant-design/icons';
import Page from 'components/Page';
import Drawer from 'components/Drawer';
import Table from 'components/Table';
import useAuth from 'hooks/useAuth';

import useUser from 'hooks/useUser';
import StatusTag from 'components/StatusTag';
import { Mfa, UserStatus } from 'shared/services/prisma/types';
import useGlobalContext from 'hooks/useGlobalContext';
import { renderBool } from 'components/Table/renderers';
import PhoneNumber from 'components/PhoneNumber';
import UpsertUserForm from './UpsertUserForm';
import messages from './messages';
import InviteUserForm from './InviteUserForm';

const QUERY = gql`
  query GetUsers {
    users {
      id
      email
      firstname
      lastname
      phone
      mfa
      lastLogin
      loginsCount
      status
      invitationUrl
      roles {
        id
        name
      }
      customerAccess {
        id
        customer {
          id
          name
        }
      }
    }
    roles(type: INTERNAL) {
      id
      name
    }
  }
`;

const UPDATE_USER = gql`
  mutation($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      firstname
      lastname
      phone
      mfa
      status
      roles {
        id
        name
      }
    }
  }
`;

const BLOCK_USER = gql`
  mutation($id: ID!) {
    blockUser(id: $id) {
      id
      status
    }
  }
`;

const UNBLOCK_USER = gql`
  mutation($id: ID!) {
    unblockUser(id: $id) {
      id
      status
    }
  }
`;

const DELETE_USER = gql`
  mutation($id: ID!) {
    deleteUser(id: $id)
  }
`;

const INVITE_USER = gql`
  mutation($input: InviteUserInput!) {
    inviteUser(input: $input)
  }
`;

const { Text, Paragraph } = Typography;
const Users = () => {
  const { formatMessage: $t } = useIntl();
  const [search, setSearch] = useState();
  const { data, loading, refetch } = useQuery(QUERY);
  const [updateUser, { loading: updating }] = useMutation(UPDATE_USER);
  const [inviteUser, { loading: inviting }] = useMutation(INVITE_USER);
  const [deleteUser] = useMutation(DELETE_USER);
  const [blockUser] = useMutation(BLOCK_USER);
  const [unblockUser] = useMutation(UNBLOCK_USER);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm] = Form.useForm();

  const [userToUpdate, setUserToUpdate] = useState();
  const [upsertForm] = Form.useForm();
  const { impersonate } = useAuth();
  const { user: currentUser } = useUser();
  const { modal, message } = useGlobalContext();

  const columns = [
    {
      dataIndex: 'email',
      title: <FormattedMessage {...messages.email} />,
      defaultSortOrder: 'ascend',
    },
    {
      dataIndex: 'firstname',
      title: <FormattedMessage {...messages.firstname} />,
    },
    {
      dataIndex: 'lastname',
      title: <FormattedMessage {...messages.lastname} />,
    },
    {
      dataIndex: 'phone',
      title: <FormattedMessage {...messages.phone} />,
      visible: false,
      render: (phone) => <PhoneNumber number={phone} />,
    },
    {
      dataIndex: 'roles',
      title: <FormattedMessage {...messages.authRoles} />,
      search: (roles, s) => roles.find(r => r.name.toLowerCase().search(s) !== -1),
      render: (roles) => (roles || []).map((role, i) => (
        <Tag key={i} style={{ marginBottom: 8 }}>
          {role.name}
        </Tag>
      )),
    },
    {
      dataIndex: 'customerAccess',
      title: <FormattedMessage {...globalMessages.customers} />,
      sorter: (a, b) => a.customerAccess.length - b.customerAccess.length,
      search: (customerAccess, s) => customerAccess.find(({ customer }) => customer.name.toLowerCase().search(s) !== -1),
      render: (customerAccess) => customerAccess.map(({ customer }, i) => (
        <Tag key={i} style={{ marginBottom: 8 }}>{customer.name}</Tag>),
      ),
    },
    {
      dataIndex: 'loginsCount',
      title: <FormattedMessage {...messages.loginCount} />,
      type: 'number',
      visible: false,
    },
    {
      key: 'lastLoginRelative',
      dataIndex: 'lastLogin',
      title: <FormattedMessage {...messages.lastLogin} />,
      type: 'relativeDate',
    },
    {
      key: 'lastLoginDate',
      dataIndex: 'lastLogin',
      title: <FormattedMessage {...messages.lastLoginDate} />,
      type: 'datetime',
      visible: false,
    },
    {
      dataIndex: 'mfa',
      title: 'MFA',
      type: 'bool',
      render: mfa => renderBool(!!mfa),
    },
    {
      dataIndex: 'status',
      title: <FormattedMessage {...globalMessages.status} />,
      render: (status) => <StatusTag status={status} />,
      align: 'center',
    },
  ];

  const onInvite = async (values) => {
    try {
      const result = await inviteUser({
        variables: { input: values },
      });

      if (result.data.inviteUser === UserStatus.Active) {
        message.success($t(messages.alreadyActive, { email: values.email }));
      } else {
        message.success($t(messages.invitationSent, { email: values.email }));
      }
      setShowInvite(false);
      refetch();
    } catch (error) {
      // TODO: This doesn't work, the error is somehow caught by ErrorBoundary before it gets here
      console.log(error);
      message.error($t(messages.inviteUserFailed));
    }
  };

  const onCopyLink = (link) => {
    try {
      copy(link, {
        format: 'text/plain',
        onCopy: () => message.info($t(messages.linkCopied)),
      });
    } catch (error) {
      message.error(
        <>
          <Text>{$t(messages.linkCopyFailed)}</Text>
          <Paragraph copyable={{ tooltips: false }}>{link}</Paragraph>
        </>,
      );
    }
  };

  const onUpdate = async (values) => {
    try {
      await updateUser({
        variables: { input: { id: userToUpdate.id, ...values } },
      });
      setUserToUpdate(false);
      message.success($t(globalMessages.successfullFormFeedback));
    } catch (error) {
      message.error($t(messages.upsertUserFailed));
    }
  };

  const onBlock = async (userId) => {
    await blockUser({
      variables: { id: userId },
    });
  };

  const onUnblock = async (userId) => {
    await unblockUser({
      variables: { id: userId },
    });
  };

  const onDisableMfa = async (userId) => {
    await updateUser({
      variables: {
        input: { id: userId, mfa: null },
      },
    });
  };

  const onEnableMfa = async (userId) => {
    await updateUser({
      variables: {
        input: { id: userId, mfa: Mfa.Sms },
      },
    });
  };

  const onDelete = async ({ email, id }) => {
    modal.confirm({
      title: $t(messages.confirmDeleteUser, { email }),
      okText: $t(globalMessages.delete),
      okType: 'danger',
      async onOk() {
        await deleteUser({ variables: { id } });
        refetch();
      },
    });
  };

  const actions = (user) => (
    <Button icon={<EditFilled />} onClick={() => setUserToUpdate(user)}>
      <FormattedMessage {...globalMessages.edit} />
    </Button>
  );

  const actionsMenuItems = (user) => [{
    key: 'impersonate',
    label: <FormattedMessage {...messages.impersonate} />,
    onClick: () => impersonate(user.id),
    icon: <UserSwitchOutlined />,
    visible: user.id !== currentUser.id,
  }, {
    key: 'copyInvitation',
    label: <FormattedMessage {...messages.copyInvitationLink} />,
    onClick: () => onCopyLink(user.invitationUrl),
    icon: <LinkOutlined />,
    visible: user.status === UserStatus.Invited,
  }, {
    key: 'unblock',
    label: <FormattedMessage {...messages.unblockUser} />,
    onClick: () => onUnblock(user.id),
    icon: <UnlockOutlined />,
    visible: user.status === UserStatus.Blocked,
  }, {
    key: 'block',
    label: <FormattedMessage {...messages.blockUser} />,
    onClick: () => onBlock(user.id),
    icon: <StopOutlined />,
    visible: user.status !== UserStatus.Blocked && user.id !== currentUser.id,
  }, {
    key: 'disableMfa',
    label: 'Disable MFA',
    onClick: () => onDisableMfa(user.id),
    icon: <StopOutlined />,
    visible: user.mfa != null,
  }, {
    key: 'enableMfa',
    label: 'Enable MFA',
    onClick: () => onEnableMfa(user.id),
    icon: <CheckOutlined />,
    visible: user.mfa == null && user.phone != null,
  }, {
    key: 'remove',
    label: <FormattedMessage {...globalMessages.delete} />,
    onClick: () => onDelete(user),
    danger: true,
    icon: <DeleteFilled />,
    visible: user.id !== currentUser.id,
  }];

  const headerActions = (
    <Space layout="horizontal">
      <Button type="primary" onClick={() => setShowInvite(true)} icon={<UserAddOutlined />}>
        <FormattedMessage {...messages.inviteUser} />
      </Button>
    </Space>
  );

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage {...globalMessages.users} />}
        extra={headerActions}
      />
      <Page.Content>
        <Input.Search
          onSearch={setSearch}
          allowClear
          placeholder={$t(globalMessages.search)}
          style={{ marginBottom: '1em' }}
        />

        <Table
          loading={loading}
          columns={columns}
          dataSource={data?.users}
          search={search}
          actions={actions}
          actionsMenuItems={actionsMenuItems}
        />
      </Page.Content>
      <Drawer
        title={
          userToUpdate?.id
            ? <FormattedMessage {...messages.editUser} />
            : <FormattedMessage {...messages.createUser} />
        }
        open={!!userToUpdate}
        onClose={() => setUserToUpdate(false)}
        onCancel={() => setUserToUpdate(false)}
        onOk={upsertForm.submit}
        confirmLoading={updating}
        destroyOnClose
      >
        <UpsertUserForm
          user={userToUpdate}
          form={upsertForm}
          onSubmit={onUpdate}
          availableRoles={data?.roles}
        />
      </Drawer>
      <Drawer
        title={<FormattedMessage {...messages.inviteUser} />}
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onCancel={() => setShowInvite(false)}
        onOk={inviteForm.submit}
        confirmLoading={inviting}
        okLabel={<FormattedMessage {...messages.sendInvitation} />}
        destroyOnClose
      >
        <InviteUserForm form={inviteForm} onSubmit={onInvite} availableRoles={data?.roles} />
      </Drawer>
    </Page>
  );
};

export default Users;
